'use client';

import { useLocale } from 'next-intl';
import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';

import { useCountry } from '@/queries/Countries';
import { Country } from '@/queries/types/graphql';

export interface GeoProviderProps extends PropsWithChildren {
  readonly countryCode: string;
}

interface GeoContext {
  countryCode: string;
  countryData: Country;
  locale: string;
}

const Context = createContext<GeoContext | null>(null);

export const useGeoContext = () => {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('Did you forget to include GeoProvider in your tree?');
  }
  return ctx;
};

const GeoProvider = ({
  children,
  countryCode = 'US',
}: GeoProviderProps): JSX.Element => {
  const locale = useLocale();
  const {
    data: countryData = {
      countryCode,
      currency: 'USD',
      eu: false,
    },
  } = useCountry(countryCode);

  return (
    <Context.Provider value={{ countryCode, countryData, locale }}>
      {children}
    </Context.Provider>
  );
};

export default GeoProvider;
